import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, AlertCircle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SignatureCanvas from '../components/SignatureCanvas';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import { DatePickerFr } from '../components/ui/DatePickerFr';
import { getToken } from '../services/tokenService';

const DOCUMENT_TYPES = [
  {
    id: 'bulletin_adhesion',
    name: "Bulletin d'Adhésion",
    description: "Document d'adhésion au groupement d'employeurs",
    icon: '📋',
  },
  {
    id: 'conditions_generales',
    name: 'Conditions Générales',
    description: 'Conditions générales de mise à disposition de personnel',
    icon: '📜',
  },
  {
    id: 'convention_mise_disposition',
    name: 'Convention de Mise à Disposition',
    description: 'Convention individuelle pour mise à disposition de salarié',
    icon: '🤝',
  },
  {
    id: 'fiche_bilan_mission',
    name: 'Fiche de Bilan de Mission',
    description: 'Bilan et évaluation de la mission effectuée',
    icon: '📊',
  },
];

export default function ContractGenerator() {
  const _navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({});
  const [showSignature, setShowSignature] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setFormData({});
    setSignatureData(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSignatureSave = (signature) => {
    setSignatureData(signature);
    setShowSignature(false);
    toast.success('Signature enregistrée avec succès');
  };

  const handleGenerate = async () => {
    if (!signatureData) {
      toast.error('Veuillez signer le document avant de générer');
      return;
    }

    setIsGenerating(true);

    try {
      const token = getToken();
      
      // Préparer les données avec signature
      const payload = {
        ...formData,
        signature: {
          nom: formData.signer_name || '',
          email: formData.signer_email || '',
          signature_image: signatureData,
          ip_address: 'unknown',
          user_agent: navigator.userAgent,
        }
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contracts/generate/${selectedType}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la génération');
      }

      // Télécharger le PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedType}_${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Document généré avec succès !');
      
      // Réinitialiser le formulaire
      setSelectedType(null);
      setFormData({});
      setSignatureData(null);
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la génération du document');
    } finally {
      setIsGenerating(false);
    }
  };

  const renderFormFields = () => {
    if (!selectedType) return null;

    const commonFields = (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom du signataire *
            </label>
            <input
              type="text"
              value={formData.signer_name || ''}
              onChange={(e) => handleInputChange('signer_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email du signataire *
            </label>
            <input
              type="email"
              value={formData.signer_email || ''}
              onChange={(e) => handleInputChange('signer_email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        </div>
      </>
    );

    switch (selectedType) {
      case 'bulletin_adhesion':
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raison sociale *
                </label>
                <input
                  type="text"
                  value={formData.raison_sociale || ''}
                  onChange={(e) => handleInputChange('raison_sociale', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SIRET *
                </label>
                <input
                  type="text"
                  value={formData.siret || ''}
                  onChange={(e) => handleInputChange('siret', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse *
              </label>
              <input
                type="text"
                value={formData.adresse || ''}
                onChange={(e) => handleInputChange('adresse', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Représentant légal *
                </label>
                <input
                  type="text"
                  value={formData.representant || ''}
                  onChange={(e) => handleInputChange('representant', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={formData.telephone || ''}
                  onChange={(e) => handleInputChange('telephone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant cotisation annuelle (€ Montant de base)
              </label>
              <input
                type="number"
                value={formData.montant_cotisation || '600'}
                onChange={(e) => handleInputChange('montant_cotisation', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motifs d'adhésion
              </label>
              <textarea
                value={formData.motifs_adhesion || ''}
                onChange={(e) => handleInputChange('motifs_adhesion', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {commonFields}
          </>
        );

      case 'convention_mise_disposition':
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raison sociale *
                </label>
                <input
                  type="text"
                  value={formData.raison_sociale || ''}
                  onChange={(e) => handleInputChange('raison_sociale', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SIRET *
                </label>
                <input
                  type="text"
                  value={formData.siret || ''}
                  onChange={(e) => handleInputChange('siret', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse *
              </label>
              <input
                type="text"
                value={formData.adresse || ''}
                onChange={(e) => handleInputChange('adresse', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Objet de la mission *
              </label>
              <input
                type="text"
                value={formData.objet_mission || ''}
                onChange={(e) => handleInputChange('objet_mission', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description de la mission
              </label>
              <textarea
                value={formData.description_mission || ''}
                onChange={(e) => handleInputChange('description_mission', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date début
                </label>
                <DatePickerFr
                  value={formData.date_debut || ''}
                  onChange={(v) => handleInputChange('date_debut', v)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date fin
                </label>
                <DatePickerFr
                  value={formData.date_fin || ''}
                  onChange={(v) => handleInputChange('date_fin', v)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Taux horaire (€)
                </label>
                <input
                  type="number"
                  value={formData.tarif_horaire || ''}
                  onChange={(e) => handleInputChange('tarif_horaire', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            {commonFields}
          </>
        );

      case 'fiche_bilan_mission':
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raison sociale *
                </label>
                <input
                  type="text"
                  value={formData.raison_sociale || ''}
                  onChange={(e) => handleInputChange('raison_sociale', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du salarié
                </label>
                <input
                  type="text"
                  value={formData.nom_salarie || ''}
                  onChange={(e) => handleInputChange('nom_salarie', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Poste
                </label>
                <input
                  type="text"
                  value={formData.poste || ''}
                  onChange={(e) => handleInputChange('poste', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date début
                </label>
                <DatePickerFr
                  value={formData.date_debut || ''}
                  onChange={(v) => handleInputChange('date_debut', v)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date fin
                </label>
                <DatePickerFr
                  value={formData.date_fin || ''}
                  onChange={(v) => handleInputChange('date_fin', v)}
                  className="w-full"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Évaluation de la performance
              </label>
              <textarea
                value={formData.evaluation || ''}
                onChange={(e) => handleInputChange('evaluation', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Décrivez la performance du salarié pendant la mission..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observations
              </label>
              <textarea
                value={formData.observations || ''}
                onChange={(e) => handleInputChange('observations', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Observations complémentaires..."
              />
            </div>
            {commonFields}
          </>
        );

      case 'conditions_generales':
        return commonFields;

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Bouton retour */}
        <BackButton label="Retour" className="mb-4" />
        
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Générateur de Contrats O'SCOP GE
          </h1>
          <p className="text-gray-600">
            Générez et signez électroniquement vos documents contractuels
          </p>
        </div>

        {!selectedType ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {DOCUMENT_TYPES.map((type) => (
              <Card
                key={type.id}
                className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-400"
                onClick={() => handleTypeSelect(type.id)}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <span className="text-3xl">{type.icon}</span>
                    <div>
                      <div className="text-lg">{type.name}</div>
                      <div className="text-sm font-normal text-gray-600 mt-1">
                        {type.description}
                      </div>
                    </div>
                  </CardTitle>
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-6 h-6 text-blue-600" />
                    {DOCUMENT_TYPES.find((t) => t.id === selectedType)?.name}
                  </CardTitle>
                  <Button variant="outline" onClick={() => setSelectedType(null)}>
                    Changer de type
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {renderFormFields()}
              </CardContent>
            </Card>

            {!showSignature && !signatureData && (
              <Card>
                <CardContent className="py-6">
                  <Button
                    onClick={() => setShowSignature(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Signer le document
                  </Button>
                </CardContent>
              </Card>
            )}

            {showSignature && (
              <Card>
                <CardHeader>
                  <CardTitle>Signature électronique</CardTitle>
                </CardHeader>
                <CardContent>
                  <SignatureCanvas
                    onSave={handleSignatureSave}
                    onCancel={() => setShowSignature(false)}
                  />
                </CardContent>
              </Card>
            )}

            {signatureData && !showSignature && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-green-700">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">Document signé</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSignature(true)}
                    >
                      Modifier la signature
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {signatureData && (
              <Card>
                <CardContent className="py-6">
                  <Button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg"
                  >
                    {isGenerating ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Génération en cours...
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5 mr-2" />
                        Générer et télécharger le PDF
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}

            <div className="flex items-start gap-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium">Informations importantes :</p>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  <li>Le document sera horodaté et signé électroniquement</li>
                  <li>Un QR code de vérification sera ajouté au document</li>
                  <li>Le contrat sera enregistré dans votre liste de documents</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
